import React, { useState, useEffect, useCallback } from 'react';
import { getFirestore, collection, addDoc, query, orderBy, onSnapshot, serverTimestamp, doc, deleteDoc } from 'firebase/firestore';
import { Zap, History, XCircle, Trash2, Lightbulb } from 'lucide-react';
import { apiCall, getErrorMessage } from './utils/api';
import { db } from './firebase';
const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

// Helper to convert resume data object to a string
const formatResumeDataAsText = (data) => {
    if (!data || !data.personalInfo) return "";
    let text = `Name: ${data.personalInfo.name}\nEmail: ${data.personalInfo.email}\nPhone: ${data.personalInfo.phone}\nLocation: ${data.personalInfo.location}\n\n`;
    if (data.goal) text += `## Goal\n${data.goal}\n\n`;
    text += "## Work Experience\n";
    (data.experience || []).forEach(exp => {
        text += `- ${exp.title} at ${exp.company} (${exp.period})\n  ${exp.description}\n`;
    });
    text += "\n## Education\n";
    (data.education || []).forEach(edu => {
        text += `- ${edu.degree} from ${edu.school} (${edu.period})\n  ${edu.description}\n`;
    });
    text += "\n## Projects\n";
    (data.projects || []).forEach(proj => {
        text += `- ${proj.title} (${proj.period})\n  ${proj.description}\n`;
    });
    text += "\n## Skills\n";
    {
        const skills = Array.isArray(data.skills)
            ? data.skills.filter(Boolean).map(String)
            : (typeof data.skills === 'string'
                ? data.skills.split(',').map(s => s.trim()).filter(Boolean)
                : []);
        text += skills.join(', ');
    }
    text += "\n\n## Achievements\n";
    (data.achievements || []).forEach(ach => {
        text += `- ${ach.description}\n`;
    });
    text += "\n## Awards\n";
    (data.awards || []).forEach(award => {
        text += `- ${award.description}\n`;
    });
    text += "\n## Certificates\n";
    (data.certificates || []).forEach(cert => {
        text += `- ${cert.description}\n`;
    });
    text += "\n## Hobbies\n";
    (data.hobbies || []).forEach(hobby => {
        text += `- ${hobby.description}\n`;
    });
    return text;
};

// --- Component to handle the "breakdown" object by mapping its entries ---
const FeedbackBreakdown = ({ breakdown }) => {
    if (!breakdown || typeof breakdown !== 'object' || Array.isArray(breakdown)) return null;

    return (
        <div className="space-y-4">
            {Object.entries(breakdown).map(([key, value]) => (
                <div key={key} className="p-4 bg-gray-800 rounded-lg border border-gray-700 shadow-md">
                    <h5 className="flex items-center gap-2 font-bold text-green-400 mb-2 capitalize">
                        <Lightbulb size={18} />
                        {key.replace(/([A-Z])/g, ' $1').trim()}
                    </h5>
                    <p className="text-gray-300 text-sm whitespace-pre-wrap">{String(value)}</p>
                </div>
            ))}
        </div>
    );
};

// -----------------------------------------------------------------------------------

const ResumeScoring = ({ builderData, trigger, user, appId }) => {

    const [isLoading, setIsLoading] = useState(false);
    const [analysisType, setAnalysisType] = useState('upload');
    const [uploadedFile, setUploadedFile] = useState(null);
    const [error, setError] = useState(null);
    const [scoreData, setScoreData] = useState(null);
    const [scoreHistory, setScoreHistory] = useState([]);

    useEffect(() => {
        if (user) {
            const q = query(collection(db, `artifacts/${appId}/users/${user.uid}/scored_resumes`), orderBy("timestamp", "desc"));
            const unsubscribe = onSnapshot(q, (querySnapshot) => {
                const history = [];
                console.log("Fetched scoring history snapshot:", querySnapshot.docs);
                querySnapshot.forEach((doc) => {
                    history.push({ id: doc.id, ...doc.data() });
                });
                setScoreHistory(history);
            });
            return () => unsubscribe();
        }
    }, [user, db, appId]);

    const handleDeleteScore = async (itemId) => {
        if (window.confirm("Are you sure you want to delete this scoring history?")) {
            const sessionDocRef = doc(db, `artifacts/${appId}/users/${user.uid}/scored_resumes`, itemId);
            try {
                await deleteDoc(sessionDocRef);
            } catch (err) {
                console.error("Error deleting session:", err);
                setError("Could not delete the session history.");
            }
        }
    };

    const handleAnalyze = useCallback(async (type) => {
        if (!user) {
            setError("Cannot analyze: User is not signed in.");
            return;
        }
        
        const currentAnalysisType = type || analysisType;
        if (currentAnalysisType === 'upload' && !uploadedFile) {
            setError("Please upload a resume file first.");
            return;
        }
        setIsLoading(true);
        setError(null);
        setScoreData(null);

        const formData = new FormData();
        let sourceName = 'AI Builder';
        if (currentAnalysisType === 'upload') {
            formData.append('resume_file', uploadedFile);
            sourceName = uploadedFile.name;
        } else {
            const resumeText = formatResumeDataAsText(builderData);
            if (!resumeText) {
                setError("Resume data from the builder is empty. Please fill out your resume first.");
                setIsLoading(false);
                return;
            }
            formData.append('resume_text', resumeText);
        }

        try {
            const { data: result } = await apiCall('/api/score-resume', {
                method: 'POST',
                body: formData,
            });
            console.log("Score resume API result:", result);

            let normalizedScoreData;
            if (result.summary !== undefined && result.breakdown !== undefined) {
                normalizedScoreData = {
                    overall_score: result.score || result.overall_score || 0,
                    summary: result.summary,
                    breakdown: result.breakdown,
                    feedback_by_category: result.feedback_by_category || null,
                };
            } else if (result.overall_score !== undefined && result.feedback_by_category !== undefined) {
                normalizedScoreData = result;
            } else {
                console.warn("API returned unfamiliar data structure:", result);
                throw new Error("Received an unexpected analysis format from the server.");
            }

            setScoreData(normalizedScoreData);

            const scoredResumesCol = collection(db, `artifacts/${appId}/users/${user.uid}/scored_resumes`);
            await addDoc(scoredResumesCol, {
                scoreData: normalizedScoreData,
                timestamp: serverTimestamp(),
                source: sourceName
            });

            setUploadedFile(null);

        } catch (err) {
            console.error("Error scoring resume:", err);
            setError(err.message);
        } finally {
            setIsLoading(false);
        }
    }, [user, analysisType, uploadedFile, builderData, db, appId]);

    useEffect(() => {
        if (trigger && trigger.timestamp && user) {
            setAnalysisType('builder');
            handleAnalyze('builder');
        }
    }, [trigger, user, handleAnalyze]);

    const handleFileChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            setUploadedFile(file);
            setError(null);
        }
    };

    const handleViewHistory = (historyItem) => {
        setScoreData(historyItem.scoreData);
    };

    const scoreValue = scoreData?.overall_score || (scoreData?.score ? scoreData.score : undefined);

    return (
        <div>
            <header className="mb-8">
                <h2 className="text-3xl font-bold text-black-500">AI Resume Score</h2>
                <p className="text-gray-400 mt-1 text-[#b473e6]/80">Get instant feedback on your resume to improve your chances.</p>
            </header>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                <div className="lg:col-span-2 grid grid-cols-1 md:grid-cols-2 gap-8">
                    <div className="bg-[#3C0F6C] p-6 rounded-xl border border-gray-700 shadow-sm">
                        <h3 className="text-xl font-bold mb-4 text-[#b473e6]">Provide Your Resume</h3>
                        <div className="mb-4 border-b border-gray-700">
                            <nav className="-mb-px flex space-x-6">
                                <button onClick={() => setAnalysisType('upload')} className={`py-3 px-1 border-b-2 font-semibold text-sm ${analysisType === 'upload' ? 'border-yellow-500 text-yellow-400' : 'border-transparent text-gray-500 hover:text-gray-300 hover:border-gray-600'}`}>
                                    Upload Resume
                                </button>
                                <button onClick={() => setAnalysisType('builder')} className={`py-3 px-1 border-b-2 font-semibold text-sm ${analysisType === 'builder' ? 'border-yellow-500 text-yellow-400' : 'border-transparent text-gray-500 hover:text-gray-300 hover:border-gray-600'}`}>
                                    Use AI Builder
                                </button>
                            </nav>
                        </div>

                        {analysisType === 'upload' ? (
                            <div>
                                <label className="text-sm font-medium text-gray-400 block mb-1">Resume File</label>
                                <input
                                    type="file"
                                    onChange={handleFileChange}
                                    accept=".pdf,.doc,.docx"
                                    className="block w-full text-sm text-gray-400 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-gray-800 file:text-gray-200 hover:file:bg-gray-700"
                                />
                                {uploadedFile && <p className="mt-2 text-sm text-gray-400">Selected: {uploadedFile.name}</p>}
                            </div>
                        ) : (
                            <div className="bg-[#3C0F6C]/50 p-4 rounded-lg text-center">
                                <p className="text-sm text-gray-400">The current resume from the AI Builder will be used for analysis.</p>
                            </div>
                        )}

                        {error && <p className="text-red-500 text-sm mt-2">{error}</p>}

                        <button
                            onClick={() => handleAnalyze()}
                            disabled={isLoading}
                            className="w-full mt-4 bg-yellow-500 text-gray-900 font-semibold py-2.5 rounded-lg hover:bg-yellow-600 transition-colors flex items-center justify-center space-x-2 disabled:bg-gray-600 disabled:cursor-not-allowed"
                        >
                            {isLoading ? (
                                <svg className="animate-spin h-5 w-5 text-black-900" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                </svg>
                            ) : (
                                <Zap size={18} />
                            )}
                            <span>{isLoading ? 'Analyzing...' : 'Analyze Resume'}</span>
                        </button>
                    </div>

                    <div className="bg-[#3C0F6C] p-6 rounded-xl border border-gray-700 shadow-sm">
                        <h3 className="text-xl font-bold mb-4 text-white">Analysis Results</h3>
                        {isLoading ? (
                            <div className="text-center py-8">
                                <svg className="animate-spin h-8 w-8 text-green-500 mx-auto" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path></svg>
                                <p className="mt-2 text-gray-400">Scoring your resume...</p>
                            </div>
                        ) : scoreData ? (
                            <div>
                                <div className="text-center mb-6">
                                    <p className="text-gray-400 font-semibold">Overall Score</p>
                                    <p className="text-6xl font-bold text-white">{scoreValue || 'N/A'}</p>
                                </div>

                                {scoreData.summary && (
                                    <div className="p-4 bg-gray-800 rounded-lg mb-4">
                                        <h4 className="font-semibold text-white">Summary</h4>
                                        <p className="text-gray-400 text-sm whitespace-pre-wrap">{scoreData.summary}</p>
                                    </div>
                                )}

                                {scoreData.breakdown && (
                                    <div>
                                        <h4 className="font-semibold text-white mb-2">Detailed Breakdown</h4>
                                        <FeedbackBreakdown breakdown={scoreData.breakdown} />
                                    </div>
                                )}

                                {scoreData.feedback_by_category && (
                                    <div className="space-y-4">
                                        {scoreData.feedback_by_category.map((item, index) => (
                                            <FeedbackItem key={index} item={item} />
                                        ))}
                                    </div>
                                )}
                            </div>
                        ) : (
                            <div className="bg-[#3C0F6C] p-6 rounded-xl border border-gray-700 shadow-sm h-96 overflow-y-auto">
                                <p className="text-white">Your analysis results will appear here.</p>
                            </div>
                        )}
                    </div>
                </div>

                <div className="bg-[#3C0F6C] p-6 rounded-xl border border-gray-700 shadow-sm">
                    <h3 className="text-xl font-bold mb-4 flex items-center space-x-2 text-white"><History size={20} /><span>Scoring History</span></h3>
                    <div className="space-y-3 max-h-96 overflow-y-auto">
                        {scoreHistory.length > 0 ? (
                            scoreHistory.map(item => (
                                <HistoryItem key={item.id} item={item} onViewDetails={() => handleViewHistory(item)} onDelete={handleDeleteScore} />
                            ))
                        ) : (
                            <p className="text-sm text-gray-500">No scoring history yet.</p>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

const HistoryItem = ({ item, onViewDetails, onDelete }) => (
    <div className="bg-gray-800 p-3 rounded-lg flex items-center justify-between">
        <div>
            <p className="font-semibold text-sm truncate w-32 text-white" title={item.source}>
                {item.source}
            </p>
            <p className="text-xs text-gray-400">
                {item.timestamp
                    ? new Date(item.timestamp.seconds * 1000).toLocaleDateString()
                    : "Just now"}
            </p>
        </div>

        <div className="text-right space-x-2">
            <p className="font-bold text-white">{item.scoreData?.overall_score || item.scoreData?.score || 'N/A'}</p>
            <button onClick={onViewDetails} className="text-xs text-green-400 hover:underline">View Details</button>
            <button onClick={() => onDelete(item.id)} className="text-xs text-red-400 hover:underline">Delete</button>
        </div>
    </div>
);

const FeedbackItem = ({ item }) => {
    const rating = item.rating.toLowerCase();
    const statusColors = {
        excellent: 'border-l-4 border-green-500 bg-green-500/10',
        good: 'border-l-4 border-blue-500 bg-blue-500/10',
        average: 'border-l-4 border-yellow-500 bg-yellow-500/10',
        poor: 'border-l-4 border-red-500 bg-red-500/10',
    };

    return (
        <div className={`p-4 rounded-lg flex items-start space-x-4 ${statusColors[rating] || 'bg-gray-800'}`}>
            <div>
                <h4 className="font-semibold text-white">{item.category}</h4>
                <p className="text-gray-400 text-sm">{item.feedback_text}</p>
            </div>
            <div className="ml-auto text-right flex-shrink-0">
                <p className="font-bold text-white">{item.score}/100</p>
                <p className={`text-xs font-semibold capitalize ${statusColors[rating] || 'bg-gray-700'} px-2 py-0.5 rounded-full`}>{item.rating}</p>
            </div>
        </div>
    );
};

export default ResumeScoring;
